import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { TextNode } from 'lexical';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDebounceState } from '@/hooks';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import './index.scss';
import { $createHashtagNode } from '../../nodes/HashtagNode';
import { HashtagOption } from '../../components/HashtagOption';
import { tagListSearch } from '../../hooks/useSearch';
import { TagInfo } from '../../types';
const PUNCTUATION = '_';

const HASHTAG_ANCHOR_CLASS_NAME = 'rich-text-editor-hashtag';

const specialPunctuationReg = /^[\w_]+$/;
const allIsNumberReg = /^\d+$/;
const isSpecialNumber = /^\d+[kKbBmM]$/;

export const hashtagStringLengthLimit = 40;
const DocumentMentionsRegex = {
  PUNCTUATION,
};

export const isValidHashtag = (hashtag: string) => {
  return (
    hashtag.length >= 1 &&
    hashtag.length <= hashtagStringLengthLimit &&
    specialPunctuationReg.test(hashtag) &&
    !allIsNumberReg.test(hashtag) &&
    !isSpecialNumber.test(hashtag)
  );
};

type PrefixType = '#' | '$';

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = ['#|$'].join('');

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]';

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  '(?:' +
  // '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  // ' |' + // E.g. " " in "Josh Duck"
  '[' +
  PUNC +
  ']|' + // E.g. "-' in "Salier-Hellendag"
  ')';

const LENGTH_LIMIT = 50;

const AtSignMentionsRegex = new RegExp(
  '(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    VALID_JOINS +
    '){0,' +
    LENGTH_LIMIT +
    '})' +
    ')$',
);

const SUGGESTION_LIST_LENGTH_LIMIT = 10;

const mentionsCache = new Map();

const dummyLookupService = {
  search(string: string, callback: (results: Array<TagInfo>) => void): void {
    tagListSearch(string).then(res => {
      if (!res?.tags?.length) {
        callback([
          {
            id: string.toLowerCase(),
            name: string,
          },
        ]);
        return;
      }
      callback(
        (res.tags || []).map(tag => ({
          id: tag.id,
          name: tag.name || tag.id,
        })),
      );
    });
  },
};

function useMentionLookupService(hashtagString: string | null) {
  const [results, setResults] = useState<Array<TagInfo>>([]);

  useEffect(() => {
    const cachedResults = mentionsCache.get(hashtagString);

    if (hashtagString == null || !hashtagString.length) {
      setResults([]);
      return;
    }

    if (cachedResults === null) {
      return;
    } else if (cachedResults !== undefined) {
      setResults(cachedResults);
      return;
    }
    const fullMatchResult = [{ id: hashtagString.toLowerCase(), name: hashtagString }];
    mentionsCache.set(hashtagString, fullMatchResult);
    setResults(fullMatchResult);
    dummyLookupService.search(hashtagString, newResults => {
      const list =
        newResults[0].id === fullMatchResult[0].id
          ? newResults
          : [...fullMatchResult, ...newResults];
      mentionsCache.set(hashtagString, list);
      setResults(list);
    });
  }, [hashtagString]);

  return results;
}

function checkForAtSignMentions(
  text: string,
  setPrefix: (prefix: PrefixType) => void,
): MenuTextMatch | null {
  const match = AtSignMentionsRegex.exec(text);
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    // const maybeLeadingWhitespace = match[1];
    const matchingString = match[2];
    if (isValidHashtag(matchingString)) {
      setPrefix(match[1][0] === '$' ? '$' : '#');
      return {
        leadOffset: match.index,
        matchingString,
        replaceableString: match[1],
      };
    }
  }
  return null;
}

function getPossibleQueryMatch(
  text: string,
  setPrefix: (prefix: PrefixType) => void,
): MenuTextMatch | null {
  return checkForAtSignMentions(text, setPrefix);
}

class MentionTypeaheadOption extends MenuOption {
  id: string;
  name: string;

  constructor(tag: TagInfo) {
    super(tag.name);
    this.id = tag.id;
    this.name = tag.name;
  }
}

function MentionsTypeaheadMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  search,
  option,
  hashtagPrefix,
}: {
  index: number;
  search: string;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: MentionTypeaheadOption;
  hashtagPrefix: '#' | '$';
}) {
  let className = 'item';
  if (isSelected) {
    className += ' selected';
  }
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={className}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={'typeahead-item-' + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}>
      <HashtagOption data={option} search={search} prefix={hashtagPrefix} />
    </li>
  );
}

export default function HashtagPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [queryString, setQueryString] = useDebounceState<string | null>(null);
  const [hashtagPrefix, setHashtagPrefix] = useDebounceState<'#' | '$'>('#');

  const results = useMentionLookupService(queryString);

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
    minLength: 0,
  });

  const options = useMemo(
    () =>
      results
        .map(result => new MentionTypeaheadOption(result))
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
    [results],
  );

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        const prefix = nodeToReplace?.getTextContent().slice(0, 1);
        const mentionNode = $createHashtagNode(`${prefix}${selectedOption.name}`, selectedOption);
        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode);
          const emptyNode = new TextNode(' ');
          mentionNode.insertAfter(emptyNode);
          emptyNode.select(1, 1);
        }
        closeMenu();
      });
    },
    [editor],
  );

  const checkForMentionMatch = useCallback(
    (text: string) => {
      const slashMatch = checkForSlashTriggerMatch(text, editor);
      if (slashMatch !== null) {
        return null;
      }

      return getPossibleQueryMatch(text, setHashtagPrefix);
    },
    [checkForSlashTriggerMatch, editor, setHashtagPrefix],
  );

  const clearEditing = useCallback(
    (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current?.contains(e.target as Node) &&
        document.querySelector(`.${HASHTAG_ANCHOR_CLASS_NAME}`)
      ) {
        setQueryString('');
      }
    },
    [setQueryString],
  );

  useEffect(() => {
    window.addEventListener('click', clearEditing);
    return () => {
      window.removeEventListener('click', clearEditing);
    };
  }, [clearEditing]);

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      anchorClassName={HASHTAG_ANCHOR_CLASS_NAME}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) =>
        anchorElementRef.current && results.length
          ? ReactDOM.createPortal(
              <div ref={dropdownRef} className="typeahead-popover mentions-menu">
                <ul>
                  {options.map((option, i: number) => (
                    <MentionsTypeaheadMenuItem
                      index={i}
                      search={queryString || ''}
                      hashtagPrefix={hashtagPrefix}
                      isSelected={selectedIndex === i}
                      onClick={() => {
                        setHighlightedIndex(i);
                        selectOptionAndCleanUp(option);
                      }}
                      onMouseEnter={() => {
                        setHighlightedIndex(i);
                      }}
                      key={option.key}
                      option={option}
                    />
                  ))}
                </ul>
              </div>,
              anchorElementRef.current,
            )
          : null
      }
    />
  );
}
